import { ref } from 'vue';
import { httpService, type ApiResponse } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import type { Invoice_EmitInput } from '../contracts/Invoice_EmitInput';
import type { Invoice_FiscalRequirementsRow } from '../contracts/Invoice_FiscalRequirementsRow';

/**
 * Row shape returned by /subaccounts/invoices/lista.
 * Mirrors the widened Invoice_InvoiceRow PHP contract.
 */
export type InvoiceListRow = {
  id: number;
  franchise_id: number;
  /** Resolved franchise name, injected by the page from the active filter
   *  (not part of the backend row contract). Merged under the ID column. */
  franchise_name?: string | null;
  external_id: string | null;
  invoice_number: string | null;
  invoice_type: string | null;
  /** Raw document kind — 'nfe' | 'nfse' | 'dte'. Drives getDocumentKindLabel(). */
  document_kind: string | null;
  status: string;
  customer_doc: string | null;
  customer_doc_type: string | null;
  customer_name: string | null;
  customer_email: string | null;
  customer_phone: string | null;
  cliente_id: number | null;
  total_amount: number | null;
  tax_amount: number | null;
  taxless_amount: number | null;
  emission_date: string | null;
  authority_date: string | null;
  authority_number: string | null;
  authority_message: string | null;
  xml_url: string | null;
  pdf_url: string | null;
  provider_code: string | null;
  related_entity_type: string | null;
  related_entity_id: number | null;
  created_at: string | null;
  updated_at: string | null;
};

/**
 * Composable for managing payment invoices (NFS-e for Brazil)
 *
 * Provides operations for:
 * - Creating and managing invoices
 * - Downloading PDFs
 * - Sending invoices via email
 * - Managing fiscal configuration
 */
export function usePaymentInvoices() {
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  /**
   * Emit an invoice manually (NFS-e / DTE) for a franchise.
   * Body matches Invoice_EmitInput; the response is Invoice_EmitResponse.
   * @param input - Manual emission payload (franchise, customer, amounts, dates)
   * @returns Emitted invoice data (invoice_id, external_id, status, invoice_number)
   */
  const createInvoice = async (input: Invoice_EmitInput) => {
    log('usePaymentInvoices', 'createInvoice', 'Creating invoice', input);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post<ApiResponse>(
        '/index.php/subaccounts/invoices/criar',
        input,
        { responseContract: 'Invoice_EmitResponse', inputContract: 'Invoice_EmitInput' }
      );
      log('usePaymentInvoices', 'createInvoice', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao criar nota fiscal');
      }
    } catch (err) {
      log('usePaymentInvoices', 'createInvoice', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get invoices for a compromisso
   * @param {number} compromissoId - Compromisso ID
   * @returns {Array} Array of invoices
   */
  const getInvoicesForCompromisso = async (compromissoId: number) => {
    log('usePaymentInvoices', 'getInvoicesForCompromisso', 'Fetching invoices', compromissoId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get<ApiResponse<unknown[]>>(`/index.php/subaccounts/invoices/lista/1?related_entity_type=compromisso&related_entity_id=${compromissoId}`, { responseContract: 'Invoice_ListResponse' });
      log('usePaymentInvoices', 'getInvoicesForCompromisso', 'Response', response);

      if (response.success) {
        return response.dados || [];
      } else {
        return [];
      }
    } catch (err) {
      log('usePaymentInvoices', 'getInvoicesForCompromisso', 'Error', err);
      error.value = err.message;
      return [];
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get invoice by ID
   * @param {number} invoiceId - Invoice ID
   * @returns {Object} Invoice details
   */
  const getInvoice = async (invoiceId: number) => {
    log('usePaymentInvoices', 'getInvoice', 'Fetching invoice', invoiceId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get<ApiResponse>(`/index.php/subaccounts/invoices/detalhe/${invoiceId}`, { responseContract: 'Invoice_DetailResponse' });
      log('usePaymentInvoices', 'getInvoice', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Nota fiscal não encontrada');
      }
    } catch (err) {
      log('usePaymentInvoices', 'getInvoice', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Cancel an invoice.
   * `justificativa` is required by the backend (15-255 chars) only when the
   * franchise's provider is 'focus_nfe' (Brazil); Chile posts an empty body.
   * @param {number} invoiceId - Invoice ID
   * @param {string} justificativa - Reason for cancellation (optional; provider-conditional)
   * @returns {Object} Updated invoice
   */
  const cancelInvoice = async (invoiceId: number | string, justificativa?: string) => {
    log('usePaymentInvoices', 'cancelInvoice', 'Cancelling invoice', invoiceId);
    isLoading.value = true;
    error.value = null;

    try {
      const trimmed = justificativa?.trim();
      const body = trimmed ? { justificativa: trimmed } : {};
      const response = await httpService.post<ApiResponse>(`/index.php/subaccounts/invoices/cancelar/${invoiceId}`, body, { responseContract: 'Invoice_CancelResponse', inputContract: 'SubaccountsInvoices_CancelarInput' });
      log('usePaymentInvoices', 'cancelInvoice', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao cancelar nota fiscal');
      }
    } catch (err) {
      log('usePaymentInvoices', 'cancelInvoice', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Send invoice to customer email
   * @param {number} invoiceId - Invoice ID
   * @param {string} email - Optional specific email (uses customer email if not provided)
   * @returns {Object} Send result
   */
  const sendEmail = async (invoiceId: number | string, email: string | null = null) => {
    log('usePaymentInvoices', 'sendEmail', 'Sending email', { invoiceId, email });
    isLoading.value = true;
    error.value = null;

    try {
      const data = email ? { email } : {};
      const response = await httpService.post<ApiResponse>(`/index.php/subaccounts/invoices/send_email/${invoiceId}`, data, { responseContract: 'SubaccountsInvoices_SendEmailResponse', inputContract: 'SubaccountsInvoices_SendEmailInput' });
      log('usePaymentInvoices', 'sendEmail', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao enviar nota fiscal por email');
      }
    } catch (err) {
      log('usePaymentInvoices', 'sendEmail', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Open the invoice PDF in a new tab.
   * When the caller already holds the detail it passes `pdfUrl` directly,
   * avoiding a redundant detalhe round-trip; otherwise the detail is fetched
   * once to resolve the URL.
   * @param invoiceId - Invoice ID
   * @param pdfUrl - Known pdf_url from the caller's detail (optional)
   */
  const downloadPdf = async (invoiceId: number, pdfUrl?: string | null) => {
    log('usePaymentInvoices', 'downloadPdf', 'Downloading PDF', { invoiceId, pdfUrl });
    isLoading.value = true;
    error.value = null;

    try {
      let url = pdfUrl ?? null;
      if (!url) {
        const invoice = await getInvoice(invoiceId) as { pdf_url?: string | null } | null | undefined;
        url = invoice?.pdf_url ?? null;
      }

      if (url) {
        window.open(url, '_blank');
      } else {
        throw new Error('PDF não disponível para esta nota fiscal');
      }
    } catch (err) {
      log('usePaymentInvoices', 'downloadPdf', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Open the invoice XML in a new tab.
   * Mirrors downloadPdf: when the caller already holds the detail it passes
   * `xmlUrl` directly, avoiding a redundant detalhe round-trip; otherwise the
   * detail is fetched once to resolve the URL.
   * @param invoiceId - Invoice ID
   * @param xmlUrl - Known xml_url from the caller's detail (optional)
   */
  const downloadXml = async (invoiceId: number, xmlUrl?: string | null) => {
    log('usePaymentInvoices', 'downloadXml', 'Downloading XML', { invoiceId, xmlUrl });
    isLoading.value = true;
    error.value = null;

    try {
      let url = xmlUrl ?? null;
      if (!url) {
        const invoice = await getInvoice(invoiceId) as { xml_url?: string | null } | null | undefined;
        url = invoice?.xml_url ?? null;
      }

      if (url) {
        window.open(url, '_blank');
      } else {
        throw new Error('XML não disponível para esta nota fiscal');
      }
    } catch (err) {
      log('usePaymentInvoices', 'downloadXml', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // ============================================
  // FISCAL CONFIGURATION
  // ============================================

  /**
   * Get fiscal configuration requirements
   * @returns {Object} Fiscal config requirements based on provider/country
   */
  const getFiscalRequirements = async (): Promise<{ providers: Invoice_FiscalRequirementsRow[] } | undefined> => {
    log('usePaymentInvoices', 'getFiscalRequirements', 'Fetching requirements');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get<ApiResponse<{ providers: Invoice_FiscalRequirementsRow[] }>>('/index.php/subaccounts/fiscal/requirements', { responseContract: 'Invoice_FiscalRequirementsResponse' });
      log('usePaymentInvoices', 'getFiscalRequirements', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao buscar requisitos fiscais');
      }
    } catch (err) {
      log('usePaymentInvoices', 'getFiscalRequirements', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  type InvoiceListFilters = {
    page?: number;
    franchise_id?: number;
    status?: string;
    invoice_type?: string;
    date_from?: string;
    date_to?: string;
    customer_doc?: string;
    cliente_id?: number;
  };

  /**
   * List emitted invoices for a franchise (paginated).
   * Filters mirror Invoice_ListInput; the response contract is Invoice_ListResponse.
   */
  const listInvoices = async (
    filters: InvoiceListFilters = {}
  ): Promise<{ invoices: InvoiceListRow[]; total: number }> => {
    log('usePaymentInvoices', 'listInvoices', 'Listing invoices', filters);
    isLoading.value = true;
    error.value = null;

    try {
      const page = filters.page ?? 1;
      const params = new URLSearchParams();
      if (filters.franchise_id !== undefined) params.set('franchise_id', String(filters.franchise_id));
      if (filters.status)       params.set('status', filters.status);
      if (filters.invoice_type) params.set('invoice_type', filters.invoice_type);
      if (filters.date_from)    params.set('date_from', filters.date_from);
      if (filters.date_to)      params.set('date_to', filters.date_to);
      if (filters.customer_doc) params.set('customer_doc', filters.customer_doc);
      if (filters.cliente_id !== undefined) params.set('cliente_id', String(filters.cliente_id));
      const qs = params.toString();
      const url = `/index.php/subaccounts/invoices/lista/${page}${qs ? `?${qs}` : ''}`;

      const response = await httpService.get<ApiResponse<{ invoices?: InvoiceListRow[]; total?: number }>>(
        url,
        { responseContract: 'Invoice_ListResponse' }
      );
      log('usePaymentInvoices', 'listInvoices', 'Response', response);

      if (response.success) {
        const dados = (response.dados || {}) as { invoices?: InvoiceListRow[]; total?: number };
        return {
          invoices: dados.invoices ?? [],
          total: dados.total ?? 0,
        };
      }
      return { invoices: [], total: 0 };
    } catch (err) {
      log('usePaymentInvoices', 'listInvoices', 'Error', err);
      error.value = err.message;
      return { invoices: [], total: 0 };
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * List the FULL invoice history for a Service Order — live, cancelled and
   * errored documents, most recent first (backend order-by id DESC). Feeds
   * the notas-fiscais section on the OS checkout screen (PDVCheckoutForm),
   * so the note cited by the OS_JA_FATURADA block ("esta OS já possui a nota
   * n. X") is visible right there.
   * @param soOrderId - Service Order ID
   * @returns Invoice rows for the OS (empty array on error or no notes)
   */
  const listInvoicesForOrdemServico = async (soOrderId: number): Promise<InvoiceListRow[]> => {
    log('usePaymentInvoices', 'listInvoicesForOrdemServico', 'Listing invoices for SO', soOrderId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get<ApiResponse<{ invoices?: InvoiceListRow[]; total?: number }>>(
        `/index.php/subaccounts/invoices/lista-por-ordem-servico/${soOrderId}`,
        { responseContract: 'Invoice_ListResponse' }
      );
      log('usePaymentInvoices', 'listInvoicesForOrdemServico', 'Response', response);

      if (response.success) {
        const dados = (response.dados || {}) as { invoices?: InvoiceListRow[]; total?: number };
        return dados.invoices ?? [];
      }
      return [];
    } catch (err) {
      log('usePaymentInvoices', 'listInvoicesForOrdemServico', 'Error', err);
      error.value = err.message;
      return [];
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Shape of /subaccounts/invoices/fiscal_config/{franchise_id} GET dados.
   */
  type FiscalConfigDados = {
    franchise_id: number;
    config: Record<string, any> | null;
    is_configured: boolean;
  };

  /**
   * Row shape returned by /subaccounts/invoices/fiscal_config/list.
   */
  type FiscalConfigRow = {
    franchise_id: number;
    franchise_name: string;
    country: string | null;
    invoice_provider: string | null;
    is_configured: boolean;
  };

  /**
   * List fiscal config rows for every franchise the caller has permission on.
   */
  const listFiscalConfigs = async (): Promise<FiscalConfigRow[]> => {
    log('usePaymentInvoices', 'listFiscalConfigs', 'Listing fiscal configs');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get<ApiResponse<{ rows?: FiscalConfigRow[] }>>(
        '/index.php/subaccounts/invoices/fiscal_config/list',
        { responseContract: 'Invoice_FiscalConfigListResponse' }
      );
      log('usePaymentInvoices', 'listFiscalConfigs', 'Response', response);

      if (response.success) {
        const dados = (response.dados || {}) as { rows?: FiscalConfigRow[] };
        return dados.rows ?? [];
      }
      return [];
    } catch (err) {
      log('usePaymentInvoices', 'listFiscalConfigs', 'Error', err);
      error.value = err.message;
      return [];
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get fiscal configuration for an explicit franchise.
   * Backend rejects franchises outside the caller's permitted set.
   */
  const getFiscalConfig = async (franchiseId: number): Promise<FiscalConfigDados | null> => {
    log('usePaymentInvoices', 'getFiscalConfig', 'Fetching config', franchiseId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get<ApiResponse<FiscalConfigDados>>(
        `/index.php/subaccounts/invoices/fiscal_config/${franchiseId}`
      );
      log('usePaymentInvoices', 'getFiscalConfig', 'Response', response);

      if (response.success) {
        return response.dados as FiscalConfigDados;
      }
      return null;
    } catch (err) {
      log('usePaymentInvoices', 'getFiscalConfig', 'Error', err);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Save fiscal configuration for an explicit franchise.
   * franchiseId lives in the URL — backend ignores any franchise_id in the body.
   */
  const saveFiscalConfig = async (
    franchiseId: number,
    config: Record<string, any>
  ): Promise<FiscalConfigDados | null> => {
    log('usePaymentInvoices', 'saveFiscalConfig', 'Saving config', { franchiseId, config });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post<ApiResponse<FiscalConfigDados>>(
        `/index.php/subaccounts/invoices/fiscal_config/${franchiseId}`,
        config
      );
      log('usePaymentInvoices', 'saveFiscalConfig', 'Response', response);

      if (response.success) {
        return response.dados as FiscalConfigDados;
      }
      throw new Error(response.error || 'Erro ao salvar configuração fiscal');
    } catch (err) {
      log('usePaymentInvoices', 'saveFiscalConfig', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Whether the given franchise has the minimum fiscal config required to
   * emit an invoice. Reads the per-franchise GET so the answer is the same
   * one the backend's emitter uses (no client-side re-derivation).
   */
  const isFiscalConfigured = async (franchiseId: number): Promise<boolean> => {
    try {
      const dados = await getFiscalConfig(franchiseId);
      return dados?.is_configured === true;
    } catch (err) {
      return false;
    }
  };

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  type InvoiceStatusConfig = { label: string; severity: string; icon: string };

  /**
   * Get invoice status display configuration.
   * The vocabulary is the single registry/table/modal enum:
   * draft / submitted / authorized / rejected / cancelled / error.
   * @param status - Invoice status
   * @returns Status config with label, severity, icon
   */
  const getInvoiceStatusConfig = (status: string | null | undefined): InvoiceStatusConfig => {
    const configs: Record<string, InvoiceStatusConfig> = {
      'draft':      { label: 'Rascunho',  severity: 'secondary', icon: 'pi-pencil' },
      'submitted':  { label: 'Enviada',   severity: 'info',      icon: 'pi-send' },
      'authorized': { label: 'Autorizada', severity: 'success',  icon: 'pi-check-circle' },
      'rejected':   { label: 'Rejeitada', severity: 'danger',    icon: 'pi-times-circle' },
      'cancelled':  { label: 'Cancelada', severity: 'secondary', icon: 'pi-times' },
      'error':      { label: 'Erro',      severity: 'danger',    icon: 'pi-times-circle' }
    };
    return configs[status ?? ''] || { label: status ?? '', severity: 'secondary', icon: 'pi-question' };
  };

  /**
   * Get the display label for a document_kind ('nfe' | 'nfse' | 'dte').
   * @param kind - Raw document kind from the invoice row
   * @returns Human-readable document type label
   */
  const getDocumentKindLabel = (kind: string | null | undefined): string => {
    const labels: Record<string, string> = {
      nfe: 'NF-e',
      nfse: 'NFS-e',
      dte: 'DTE',
    };
    return labels[kind ?? ''] || (kind ?? '—');
  };

  /**
   * Format invoice value for display
   * @param value - Value in reais
   * @returns Formatted value
   */
  const formatValue = (value: number | null | undefined): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  /**
   * Check if a compromisso has an authorized invoice
   * @param {number} compromissoId - Compromisso ID
   * @returns {boolean} True if has authorized invoice
   */
  const hasAuthorizedInvoice = async (compromissoId: number) => {
    try {
      const invoices = await getInvoicesForCompromisso(compromissoId);
      return invoices.some((inv: any) => (inv as any).status === 'authorized');
    } catch (err) {
      return false;
    }
  };

  /**
   * Generate an invoice from a Service Order (Chile DTE).
   * All data comes from the SO — no body needed.
   * @param {number} soOrderId - Service Order ID
   * @returns {Object} Emitted invoice data with invoice_id, status, folio
   */
  const gerarNFeParaOrdemServico = async (soOrderId: number) => {
    log('usePaymentInvoices', 'gerarNFeParaOrdemServico', 'Generating invoice from SO', soOrderId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post<ApiResponse>(
        `/index.php/subaccounts/invoices/gerar-para-ordem-servico/${soOrderId}`,
        {},
        { responseContract: 'Invoice_EmitResponse', inputContract: 'SubaccountsInvoices_GerarParaOrdemServicoInput' }
      );
      log('usePaymentInvoices', 'gerarNFeParaOrdemServico', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao gerar nota fiscal a partir da ordem de serviço');
      }
    } catch (err) {
      log('usePaymentInvoices', 'gerarNFeParaOrdemServico', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    // State
    isLoading,
    error,

    // Invoice operations
    createInvoice,
    getInvoicesForCompromisso,
    getInvoice,
    cancelInvoice,
    /**
     * Generate an invoice from a Service Order (Chile DTE).
     * No body needed — all data comes from the SO.
     * @param {number} soOrderId - Service Order ID
     * @returns {Object} Emitted invoice data
     */
    gerarNFeParaOrdemServico,
    sendEmail,
    downloadPdf,
    downloadXml,

    // Invoice listing
    listInvoices,
    /**
     * Full invoice history for a Service Order (live + cancelled + errored).
     * Feeds the notas-fiscais section on PDVCheckoutForm.
     * @param {number} soOrderId - Service Order ID
     * @returns {Array} Invoice rows for the OS
     */
    listInvoicesForOrdemServico,

    // Fiscal configuration
    getFiscalRequirements,
    listFiscalConfigs,
    getFiscalConfig,
    saveFiscalConfig,
    isFiscalConfigured,

    // Utility functions
    getInvoiceStatusConfig,
    getDocumentKindLabel,
    formatValue,
    hasAuthorizedInvoice
  };
}
